/**
 * Bug Report store for managing the bug report side panel state.
 * Provides global state for opening/closing the panel and pre-populating fields.
 *
 * Features:
 * - Open/close the bug report side panel
 * - Pre-populate sandbox ID and trace ID from context
 *
 * @module stores/useBugReportStore
 */

import { create } from 'zustand';

/**
 * Bug report store state and actions
 */
interface BugReportStore {
  /** Whether the bug report panel is currently open */
  isOpen: boolean;
  /** Pre-populated sandbox ID (from sandbox detail page) */
  sandboxId: string;
  /** Pre-populated trace ID (from trace detail page or error) */
  traceId: string;

  /**
   * Open the bug report panel with optional pre-populated values
   */
  openPanel: (options?: { sandboxId?: string; traceId?: string }) => void;

  /**
   * Close the bug report panel and clear pre-populated values
   */
  closePanel: () => void;

  /**
   * Clear pre-populated values without closing
   */
  clearPrefill: () => void;
}

/**
 * Global bug report store for managing the bug report panel state.
 *
 * @example
 * // Open the bug report panel
 * useBugReportStore.getState().openPanel();
 *
 * // Open with pre-populated sandbox ID
 * useBugReportStore.getState().openPanel({ sandboxId: 'sandbox_abc123' });
 *
 * // Open with pre-populated trace ID
 * useBugReportStore.getState().openPanel({ traceId: 'abc123def456' });
 *
 * // In a component
 * const { isOpen, sandboxId, traceId, openPanel, closePanel } = useBugReportStore();
 */
export const useBugReportStore = create<BugReportStore>((set) => ({
  isOpen: false,
  sandboxId: '',
  traceId: '',

  openPanel: (options) => {
    set({
      isOpen: true,
      sandboxId: options?.sandboxId || '',
      traceId: options?.traceId || '',
    });
  },

  closePanel: () => {
    set({
      isOpen: false,
      sandboxId: '',
      traceId: '',
    });
  },

  clearPrefill: () => {
    set({
      sandboxId: '',
      traceId: '',
    });
  },
}));
